/**
 * HIPAA Session Timeout Warning Modal
 *
 * Displays a warning when the user has been inactive for 13 minutes.
 * Shows countdown to forced logout at 15 minutes.
 */

import { AlertTriangle, Clock, RefreshCw } from 'lucide-react'
import { Button } from './ui/button'

interface SessionTimeoutWarningProps {
  remainingSeconds: number
  onContinue: () => void
}

export function SessionTimeoutWarning({
  remainingSeconds,
  onContinue
}: SessionTimeoutWarningProps) {
  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md mx-4 bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-clinical-amber/10 border-b border-clinical-amber/20 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-clinical-amber/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-clinical-amber" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Session Timeout Warning</h2>
              <p className="text-xs text-muted-foreground">HIPAA Security Requirement</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-sm text-muted-foreground mb-6">
            Your session will expire due to inactivity. Click "Continue Session" to remain logged in.
          </p>

          {/* Countdown */}
          <div className="flex items-center justify-center gap-3 py-6 bg-surface rounded-xl border border-border/50 mb-6">
            <Clock className="w-6 h-6 text-clinical-coral animate-pulse" />
            <span className="text-4xl font-mono font-bold text-foreground">
              {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
            </span>
          </div>

          <p className="text-xs text-center text-muted-foreground mb-6">
            You will be automatically logged out when the timer reaches zero.
          </p>

          {/* Action */}
          <Button
            onClick={onContinue}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-xl"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Continue Session
          </Button>
        </div>

        {/* Footer */}
        <div className="bg-surface border-t border-border/50 px-6 py-3">
          <p className="text-[10px] text-center text-muted-foreground">
            HIPAA §164.312(a)(2)(iii) - Automatic Logoff after 15 minutes of inactivity
          </p>
        </div>
      </div>
    </div>
  )
}


